import {
  ChevronLeft,
  ChevronRight,
  GripVertical,
  EllipsisVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { WIDGET_WIDTHS, type WidgetWidth } from "@playbookai/types";
import { ChromeIconButton } from "../ChromeIconButton.js";
import {
  Popover,
  PopoverContent,
  PopoverMenuItem,
  PopoverSeparator,
  PopoverTrigger,
} from "../../molecules/Popover/Popover.js";
import { WIDTH_LABEL, widgetLabel } from "./helpers.js";
import type { DashboardItem } from "./types.js";

export interface CardChromeProps {
  item: DashboardItem;
  index: number;
  total: number;
  canReorder: boolean;
  handleProps?: Record<string, unknown> | undefined;
  onChangeWidth?:
    | ((instanceId: string, width: WidgetWidth) => void)
    | undefined;
  onRemove?: ((instanceId: string) => void) | undefined;
  onMove?: ((instanceId: string, dir: -1 | 1) => void) | undefined;
  onEdit?: ((instanceId: string) => void) | undefined;
}

/** Edit-mode card header: grip handle + name + kebab (width radios, Move
 *  forward/back, Remove — each gated on its callback). */
export function CardChrome({
  item,
  index,
  total,
  canReorder,
  handleProps,
  onChangeWidth,
  onRemove,
  onMove,
  onEdit,
}: CardChromeProps) {
  const name = widgetLabel(item);
  const hasMenu = !!onChangeWidth || !!onMove || !!onEdit || !!onRemove;
  return (
    <div className="flex min-h-10 items-center gap-1.5 border-b border-pb-border/70 bg-pb-card-elevated/50 px-2.5 py-1 select-none">
      {canReorder && (
        <ChromeIconButton
          {...handleProps}
          aria-label={`Drag to reorder ${name}`}
          className="shrink-0 cursor-grab touch-none motion-reduce:transition-none"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </ChromeIconButton>
      )}
      <div className="min-w-0 flex-1 truncate text-xs font-medium text-pb-text-secondary">
        {name}
      </div>
      {hasMenu && (
        <Popover align="end">
          <PopoverTrigger>
            <ChromeIconButton
              aria-label={`${name} options`}
              className="motion-reduce:transition-none"
            >
              <EllipsisVertical className="h-4 w-4" />
            </ChromeIconButton>
          </PopoverTrigger>
          <PopoverContent className="min-w-50">
            {onChangeWidth && (
              <>
                <div className="px-2.5 py-1 text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold">
                  Width
                </div>
                {WIDGET_WIDTHS.map((w) => (
                  <PopoverMenuItem
                    key={w}
                    checked={item.width === w}
                    closeOnSelect={false}
                    onClick={() => onChangeWidth(item.instanceId, w)}
                  >
                    {WIDTH_LABEL[w]}
                  </PopoverMenuItem>
                ))}
              </>
            )}
            {onMove && (
              <>
                {onChangeWidth && <PopoverSeparator />}
                <PopoverMenuItem
                  icon={ChevronLeft}
                  disabled={index === 0}
                  onClick={() => onMove(item.instanceId, -1)}
                >
                  Move back
                </PopoverMenuItem>
                <PopoverMenuItem
                  icon={ChevronRight}
                  disabled={index === total - 1}
                  onClick={() => onMove(item.instanceId, 1)}
                >
                  Move forward
                </PopoverMenuItem>
              </>
            )}
            {(onChangeWidth || onMove) && (onEdit || onRemove) && (
              <PopoverSeparator />
            )}
            {onEdit && (
              <PopoverMenuItem
                icon={Pencil}
                onClick={() => onEdit(item.instanceId)}
              >
                Edit…
              </PopoverMenuItem>
            )}
            {onRemove && (
              <PopoverMenuItem
                icon={Trash2}
                onClick={() => onRemove(item.instanceId)}
              >
                Remove widget
              </PopoverMenuItem>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
